/**
 * Dictionnaire de traduction « audit technique -> langage client ».
 *
 * Source unique partagée par l'app (modal performance, points à corriger, tableaux)
 * et par les livrables client (PDF, HTML pages de référence, CSV). Objectif : un
 * langage unique consultant/client.
 *
 * Pour chaque problème détecté, on fournit :
 * - label    : intitulé clair (sans jargon)
 * - pourquoi : impact concret côté business / visiteurs (le « pourquoi ça compte »)
 * - corriger : action recommandée, compréhensible
 * - effort   : 'rapide' | 'moyen' | 'chantier' (ordre de grandeur, pas d'estimation horaire)
 * - axe      : 'seo' | 'performance' | 'technique' | 'accessibilite' | 'social'
 *
 * Couvre les ~50 identifiants Lighthouse les plus fréquents + les catégories
 * d'erreurs SEO/technique produites par le backend + les critères SEO on-page.
 * Tout identifiant inconnu retombe sur le libellé Lighthouse d'origine (jamais
 * d'écran vide), avec effort « à évaluer ».
 */

export const EFFORT = {
  rapide: { key: 'rapide', label: 'Rapide', rank: 1, hint: 'Quelques minutes à 1 h' },
  moyen: { key: 'moyen', label: 'Moyen', rank: 2, hint: 'Demi-journée à 1 jour' },
  chantier: { key: 'chantier', label: 'Chantier', rank: 3, hint: 'Plusieurs jours / refonte' },
  variable: { key: 'variable', label: 'À évaluer', rank: 2, hint: 'Dépend du contexte' },
};

export const AXE_LABELS = {
  seo: 'SEO',
  performance: 'Performance',
  technique: 'Technique',
  accessibilite: 'Accessibilité',
  social: 'Réseaux sociaux',
  mobile: 'Mobile',
};

/** Effort -> objet normalisé (fallback sur « à évaluer »). */
export function effortInfo(effort) {
  return EFFORT[effort] || EFFORT.variable;
}

// ── 1. Opportunités PageSpeed / Lighthouse (clé = audit ID) ────────────────────

export const LIGHTHOUSE_DICT = {
  'render-blocking-resources': {
    label: 'Ressources qui bloquent l’affichage',
    pourquoi: "Des fichiers (CSS/JS) doivent être chargés avant que la page ne s’affiche : le visiteur attend devant un écran vide, surtout sur mobile.",
    corriger: 'Différer ou charger en asynchrone les scripts et styles non essentiels au premier affichage.',
    effort: 'moyen', axe: 'performance',
  },
  'unused-javascript': {
    label: 'JavaScript inutilisé',
    pourquoi: 'Du code est téléchargé sans être utilisé : la page est plus lourde et plus lente à devenir interactive.',
    corriger: 'Supprimer ou différer les scripts inutiles (souvent des extensions ou des tags marketing).',
    effort: 'moyen', axe: 'performance',
  },
  'unused-css-rules': {
    label: 'CSS inutilisé',
    pourquoi: 'Des règles de style sont chargées alors qu’elles ne servent pas à la page, ce qui la ralentit inutilement.',
    corriger: 'Purger le CSS non utilisé et ne charger que les styles nécessaires à chaque page.',
    effort: 'moyen', axe: 'performance',
  },
  'unminified-css': {
    label: 'CSS non minifié',
    pourquoi: 'Les fichiers de style ne sont pas compressés, ce qui augmente leur poids et le temps de chargement.',
    corriger: 'Activer la minification du CSS (souvent une option du thème ou d’un plugin de cache).',
    effort: 'rapide', axe: 'performance',
  },
  'unminified-javascript': {
    label: 'JavaScript non minifié',
    pourquoi: 'Les scripts ne sont pas compressés : plus de poids à télécharger, donc une page plus lente.',
    corriger: 'Activer la minification du JavaScript (plugin de cache / build front).',
    effort: 'rapide', axe: 'performance',
  },
  'uses-optimized-images': {
    label: 'Images non optimisées',
    pourquoi: 'Les images sont trop lourdes : c’est souvent la première cause de lenteur, en particulier sur mobile.',
    corriger: 'Compresser les images et les servir à la bonne taille d’affichage.',
    effort: 'rapide', axe: 'performance',
  },
  'modern-image-formats': {
    label: 'Formats d’image modernes absents',
    pourquoi: 'Les images utilisent d’anciens formats plus lourds (JPEG/PNG) au lieu de WebP/AVIF, plus légers.',
    corriger: 'Servir les images en WebP ou AVIF (plugin de conversion ou CDN d’images).',
    effort: 'moyen', axe: 'performance',
  },
  'uses-responsive-images': {
    label: 'Images surdimensionnées',
    pourquoi: 'Des images plus grandes que nécessaire sont téléchargées, gaspillant de la bande passante.',
    corriger: 'Fournir plusieurs tailles d’image (srcset) adaptées à l’écran du visiteur.',
    effort: 'moyen', axe: 'performance',
  },
  'offscreen-images': {
    label: 'Images hors écran chargées trop tôt',
    pourquoi: 'Des images situées plus bas dans la page sont chargées immédiatement, ralentissant l’affichage initial.',
    corriger: 'Activer le chargement différé (lazy-loading) des images sous la ligne de flottaison.',
    effort: 'rapide', axe: 'performance',
  },
  'efficient-animated-content': {
    label: 'Animations (GIF) trop lourdes',
    pourquoi: 'Les GIF animés sont très lourds et ralentissent la page.',
    corriger: 'Remplacer les GIF par des vidéos compressées (MP4/WebM).',
    effort: 'moyen', axe: 'performance',
  },
  'uses-text-compression': {
    label: 'Compression des fichiers désactivée',
    pourquoi: 'Les fichiers texte (HTML/CSS/JS) sont envoyés sans compression, ce qui alourdit chaque chargement.',
    corriger: 'Activer la compression Gzip ou Brotli côté serveur.',
    effort: 'rapide', axe: 'performance',
  },
  'uses-long-cache-ttl': {
    label: 'Mise en cache trop courte',
    pourquoi: 'Les ressources sont retéléchargées à chaque visite faute de cache, ralentissant les visites répétées.',
    corriger: 'Configurer une durée de cache longue pour les fichiers statiques (images, CSS, JS).',
    effort: 'rapide', axe: 'performance',
  },
  'server-response-time': {
    label: 'Serveur lent à répondre',
    pourquoi: 'Le serveur met du temps à renvoyer la page : tout le reste s’en trouve retardé.',
    corriger: 'Optimiser l’hébergement, la base de données ou activer un cache serveur.',
    effort: 'chantier', axe: 'performance',
  },
  'redirects': {
    label: 'Redirections en chaîne',
    pourquoi: 'Des redirections successives ajoutent du délai avant d’atteindre la bonne page.',
    corriger: 'Pointer directement vers l’URL finale et limiter les redirections.',
    effort: 'moyen', axe: 'performance',
  },
  'uses-rel-preconnect': {
    label: 'Connexions externes non anticipées',
    pourquoi: 'Le navigateur découvre tard les domaines externes à contacter, ajoutant de la latence.',
    corriger: 'Ajouter des indices de préconnexion (preconnect) pour les domaines tiers clés.',
    effort: 'rapide', axe: 'performance',
  },
  'uses-rel-preload': {
    label: 'Ressources clés non préchargées',
    pourquoi: 'Des ressources critiques (police, image principale) sont découvertes trop tard.',
    corriger: 'Précharger (preload) les ressources critiques au premier affichage.',
    effort: 'moyen', axe: 'performance',
  },
  'preload-lcp-image': {
    label: 'Image principale non préchargée',
    pourquoi: 'L’image la plus visible met du temps à apparaître, dégradant la perception de vitesse.',
    corriger: 'Précharger l’image principale (LCP) de la page.',
    effort: 'moyen', axe: 'performance',
  },
  'total-byte-weight': {
    label: 'Page trop lourde',
    pourquoi: 'Le poids total de la page est élevé : chargement plus long et coûteux en données mobiles.',
    corriger: 'Réduire le poids global (images, scripts, polices) en priorité sur les éléments les plus lourds.',
    effort: 'moyen', axe: 'performance',
  },
  'dom-size': {
    label: 'Page trop complexe (trop d’éléments)',
    pourquoi: 'Un nombre excessif d’éléments HTML ralentit l’affichage et les interactions.',
    corriger: 'Simplifier la structure de la page et alléger les composants superflus.',
    effort: 'chantier', axe: 'performance',
  },
  'mainthread-work-breakdown': {
    label: 'Traitement JavaScript trop intensif',
    pourquoi: 'Le navigateur passe trop de temps à exécuter du code, rendant la page lente à réagir.',
    corriger: 'Alléger et découper le JavaScript, retirer les scripts tiers superflus.',
    effort: 'chantier', axe: 'performance',
  },
  'bootup-time': {
    label: 'Temps d’exécution du JavaScript élevé',
    pourquoi: 'Le code met du temps à se lancer, retardant le moment où la page devient utilisable.',
    corriger: 'Réduire et différer le JavaScript non essentiel.',
    effort: 'chantier', axe: 'performance',
  },
  'third-party-summary': {
    label: 'Scripts tiers trop nombreux',
    pourquoi: 'Les outils externes (analytics, chat, pubs) ajoutent du poids et ralentissent la page.',
    corriger: 'Limiter, différer ou retirer les scripts tiers non indispensables.',
    effort: 'moyen', axe: 'performance',
  },
  'third-party-facades': {
    label: 'Intégrations tierces chargées trop tôt',
    pourquoi: 'Des intégrations lourdes (vidéos, widgets) se chargent immédiatement alors qu’elles pourraient attendre.',
    corriger: 'Charger ces intégrations seulement à l’interaction (façade / lazy-load).',
    effort: 'moyen', axe: 'performance',
  },
  'legacy-javascript': {
    label: 'JavaScript obsolète',
    pourquoi: 'Du code destiné à d’anciens navigateurs est servi inutilement aux navigateurs modernes.',
    corriger: 'Servir du code moderne et ne charger les compatibilités que si nécessaire.',
    effort: 'moyen', axe: 'performance',
  },
  'duplicated-javascript': {
    label: 'JavaScript dupliqué',
    pourquoi: 'Le même code est chargé plusieurs fois, gaspillant du temps et de la bande passante.',
    corriger: 'Mutualiser les bibliothèques communes pour éviter les doublons.',
    effort: 'moyen', axe: 'performance',
  },
  'font-display': {
    label: 'Texte invisible au chargement des polices',
    pourquoi: 'Le texte peut rester invisible le temps que la police se charge, gênant la lecture.',
    corriger: 'Utiliser font-display: swap pour afficher le texte immédiatement.',
    effort: 'rapide', axe: 'performance',
  },
  'critical-request-chains': {
    label: 'Chaîne de requêtes critiques trop longue',
    pourquoi: 'Des ressources dépendent les unes des autres en cascade, allongeant le chargement.',
    corriger: 'Réduire la profondeur des dépendances et précharger les ressources clés.',
    effort: 'chantier', axe: 'performance',
  },
  'largest-contentful-paint-element': {
    label: 'Élément principal lent à s’afficher (LCP)',
    pourquoi: 'Le plus gros élément visible met du temps à apparaître : c’est ce que le visiteur perçoit comme « lent ».',
    corriger: 'Optimiser et précharger cet élément (souvent une image ou un titre principal).',
    effort: 'moyen', axe: 'performance',
  },
  'layout-shift-elements': {
    label: 'Éléments qui sautent pendant le chargement',
    pourquoi: 'La mise en page bouge pendant le chargement, ce qui est désagréable et provoque des clics ratés.',
    corriger: 'Réserver l’espace des images/pubs (dimensions fixes) pour stabiliser la page.',
    effort: 'moyen', axe: 'performance',
  },
  'cumulative-layout-shift': {
    label: 'Stabilité de la mise en page',
    kind: 'metric',
    pourquoi: 'Des éléments bougent pendant le chargement (boutons qui « sautent ») : expérience frustrante et moins de confiance.',
    corriger: 'Réserver la place des images et bannières (largeur/hauteur fixes) avant qu’elles ne s’affichent.',
    effort: 'moyen', axe: 'performance',
  },
  // Indicateurs de vitesse (métriques Lighthouse — pas des « tâches », mais des mesures)
  'largest-contentful-paint': {
    label: 'Affichage du contenu principal',
    kind: 'metric',
    pourquoi: 'Moment où le visiteur voit le plus gros élément visible (souvent une image ou un titre). Google considère la page lente au-delà de 2,5 s.',
    corriger: 'Optimiser l’image ou le bloc principal au-dessus de la ligne de flottaison, réduire les scripts qui bloquent l’affichage.',
    effort: 'moyen', axe: 'performance',
  },
  'first-contentful-paint': {
    label: 'Premier contenu visible',
    kind: 'metric',
    pourquoi: 'Délai avant le tout premier texte ou image à l’écran. Au-delà d’environ 1,8 s, la page paraît lente dès l’arrivée.',
    corriger: 'Alléger le haut de page et différer ce qui n’est pas nécessaire au premier affichage.',
    effort: 'moyen', axe: 'performance',
  },
  interactive: {
    label: 'Page réactive (boutons, formulaires…)',
    kind: 'metric',
    pourquoi: 'Temps avant que la page réponde vraiment aux clics et au scroll. Si c’est long, le visiteur a l’impression que « rien ne se passe ».',
    corriger: 'Réduire le JavaScript exécuté au chargement (souvent thème, sliders, tracking).',
    effort: 'chantier', axe: 'performance',
  },
  'max-potential-fid': {
    label: 'Réactivité au premier clic',
    kind: 'metric',
    pourquoi: 'Délai maximal avant que le navigateur traite une action (clic, tap). Au-delà de 100 ms, l’interaction semble saccadée.',
    corriger: 'Limiter le travail du navigateur au chargement : moins de scripts bloquants, tâches découpées.',
    effort: 'moyen', axe: 'performance',
  },
  'total-blocking-time': {
    label: 'Périodes où la page « gèle »',
    kind: 'metric',
    pourquoi: 'Temps cumulé où le navigateur est trop occupé pour réagir — la page semble figée pendant le chargement.',
    corriger: 'Découper ou reporter les grosses tâches JavaScript après l’affichage initial.',
    effort: 'moyen', axe: 'performance',
  },
  'speed-index': {
    label: 'Remplissage visuel de l’écran',
    kind: 'metric',
    pourquoi: 'Vitesse à laquelle le contenu remplit la page. Plus c’est lent, plus le visiteur voit un écran vide ou incomplet.',
    corriger: 'Afficher d’abord le contenu utile, charger le reste ensuite.',
    effort: 'moyen', axe: 'performance',
  },
  'first-meaningful-paint': {
    label: 'Premier contenu utile',
    kind: 'metric',
    pourquoi: 'Moment où un contenu réellement utile apparaît (pas seulement un fond ou un loader).',
    corriger: 'Réduire les éléments décoratifs au chargement et prioriser le message principal.',
    effort: 'moyen', axe: 'performance',
  },
  'interaction-to-next-paint': {
    label: 'Réactivité après un clic',
    kind: 'metric',
    pourquoi: 'Délai entre une action du visiteur et la mise à jour visible de la page (nouvelle métrique clé Google).',
    corriger: 'Alléger le JavaScript déclenché par les interactions (menus, filtres, panier…).',
    effort: 'moyen', axe: 'performance',
  },
  'experimental-interaction-to-next-paint': {
    label: 'Réactivité après un clic',
    kind: 'metric',
    pourquoi: 'Délai entre une action du visiteur et la mise à jour visible de la page.',
    corriger: 'Alléger le JavaScript déclenché par les interactions.',
    effort: 'moyen', axe: 'performance',
  },
  'non-composited-animations': {
    label: 'Animations peu performantes',
    pourquoi: 'Certaines animations sollicitent trop le navigateur et provoquent des saccades.',
    corriger: 'Privilégier des animations CSS optimisées (transform/opacity).',
    effort: 'moyen', axe: 'performance',
  },
  'uses-http2': {
    label: 'Protocole HTTP/2 non utilisé',
    pourquoi: 'Sans HTTP/2, le chargement des nombreuses ressources est moins efficace.',
    corriger: 'Activer HTTP/2 (ou HTTP/3) côté serveur ou via un CDN.',
    effort: 'moyen', axe: 'performance',
  },
  'no-document-write': {
    label: 'Usage de document.write()',
    pourquoi: 'Cette technique ancienne bloque l’affichage, surtout sur connexions lentes.',
    corriger: 'Remplacer document.write() par une insertion de script moderne.',
    effort: 'moyen', axe: 'performance',
  },
  // Bonnes pratiques / sécurité
  'is-on-https': {
    label: 'Page non sécurisée (HTTPS absent)',
    pourquoi: 'Sans HTTPS, la navigation n’est pas chiffrée : Google et les navigateurs signalent le site comme « non sécurisé ».',
    corriger: 'Installer un certificat SSL et forcer la redirection vers HTTPS.',
    effort: 'moyen', axe: 'technique',
  },
  'no-vulnerable-libraries': {
    label: 'Bibliothèques JavaScript vulnérables',
    pourquoi: 'Des composants présentant des failles connues exposent le site à des risques de sécurité.',
    corriger: 'Mettre à jour les bibliothèques concernées vers une version corrigée.',
    effort: 'moyen', axe: 'technique',
  },
  'csp-xss': {
    label: 'Protection contre les injections (CSP) absente',
    pourquoi: 'Sans politique de sécurité du contenu, le site est plus exposé aux attaques par injection.',
    corriger: 'Mettre en place un en-tête Content-Security-Policy adapté.',
    effort: 'chantier', axe: 'technique',
  },
  'external-anchors-use-rel-noopener': {
    label: 'Liens externes non sécurisés',
    pourquoi: 'Des liens ouvrant un nouvel onglet sans protection peuvent poser un risque de sécurité.',
    corriger: 'Ajouter rel="noopener" aux liens externes en target="_blank".',
    effort: 'rapide', axe: 'technique',
  },
  // SEO Lighthouse
  'image-alt': {
    label: 'Images sans texte alternatif',
    pourquoi: 'Sans attribut alt, les images ne sont pas comprises par Google ni par les lecteurs d’écran.',
    corriger: 'Ajouter un texte alternatif descriptif à chaque image porteuse de sens.',
    effort: 'rapide', axe: 'seo',
  },
  'document-title': {
    label: 'Balise titre manquante',
    pourquoi: 'Le titre est l’élément le plus important pour Google et l’affichage dans les résultats.',
    corriger: 'Ajouter une balise <title> unique et descriptive.',
    effort: 'rapide', axe: 'seo',
  },
  'meta-description': {
    label: 'Meta description manquante',
    pourquoi: 'La meta description influence le taux de clic depuis Google.',
    corriger: 'Rédiger une meta description attractive de 150-160 caractères.',
    effort: 'rapide', axe: 'seo',
  },
  'link-text': {
    label: 'Textes de liens peu explicites',
    pourquoi: 'Des liens « cliquez ici » n’aident ni Google ni les visiteurs à comprendre la destination.',
    corriger: 'Utiliser des intitulés de liens descriptifs.',
    effort: 'rapide', axe: 'seo',
  },
  'crawlable-anchors': {
    label: 'Liens non explorables par Google',
    pourquoi: 'Certains liens ne peuvent pas être suivis par les moteurs, limitant l’indexation.',
    corriger: 'Utiliser de vraies balises <a href> pour les liens de navigation.',
    effort: 'moyen', axe: 'seo',
  },
  'hreflang': {
    label: 'Balises hreflang incorrectes',
    pourquoi: 'Pour un site multilingue, des hreflang erronés brouillent le ciblage par langue/pays.',
    corriger: 'Corriger les balises hreflang pour chaque version linguistique.',
    effort: 'moyen', axe: 'seo',
  },
  'viewport': {
    label: 'Balise viewport absente',
    pourquoi: 'Sans viewport, le site s’affiche mal sur mobile, ce que Google pénalise.',
    corriger: 'Ajouter la balise meta viewport adaptée au responsive.',
    effort: 'rapide', axe: 'mobile',
  },
  'color-contrast': {
    label: 'Contraste des couleurs insuffisant',
    pourquoi: 'Un texte peu contrasté est difficile, voire impossible à lire pour de nombreux visiteurs — et pénalisé par Google.',
    corriger: 'Augmenter le contraste entre le texte et l’arrière-plan (ratio recommandé : 4,5:1 pour le texte courant).',
    effort: 'rapide', axe: 'accessibilite',
  },
};

// ── 2. Catégories d'erreurs backend (clé = catégorie normalisée) ───────────────
// Le backend fournit déjà « recommendation » (comment corriger). On complète ici
// l'impact business (« pourquoi ») et l'effort. Le « corriger » du backend est
// conservé s'il existe, sinon on utilise celui défini ci-dessous.

export const ERROR_DICT = {
  'titre manquant': {
    pourquoi: 'Le titre est le premier signal envoyé à Google ; sans lui, la page est mal référencée.',
    effort: 'rapide', axe: 'seo',
  },
  'titre non optimise': {
    pourquoi: 'Un titre mal calibré réduit la visibilité et le taux de clic dans Google.',
    effort: 'rapide', axe: 'seo',
  },
  'titre trop court': {
    pourquoi: 'Un titre trop court n’exploite pas tout l’espace disponible dans Google.',
    effort: 'rapide', axe: 'seo',
  },
  'titre trop long': {
    pourquoi: 'Un titre trop long est tronqué dans les résultats de recherche.',
    effort: 'rapide', axe: 'seo',
  },
  'meta description manquante': {
    pourquoi: 'Sans meta description, Google génère un extrait aléatoire, souvent moins incitatif au clic.',
    effort: 'rapide', axe: 'seo',
  },
  'meta description non optimisee': {
    pourquoi: 'Une meta description peu travaillée fait baisser le taux de clic depuis Google.',
    effort: 'rapide', axe: 'seo',
  },
  'meta description trop courte': {
    pourquoi: 'Une description trop courte n’est pas assez incitative.',
    effort: 'rapide', axe: 'seo',
  },
  'meta description trop longue': {
    pourquoi: 'Une description trop longue est coupée dans les résultats.',
    effort: 'rapide', axe: 'seo',
  },
  'h1 manquant': {
    pourquoi: 'Le H1 indique le sujet principal de la page à Google et aux visiteurs.',
    effort: 'rapide', axe: 'seo',
  },
  'plusieurs h1 detectes': {
    pourquoi: 'Plusieurs H1 brouillent la hiérarchie du contenu pour Google.',
    effort: 'rapide', axe: 'seo',
  },
  'structure de headings incorrecte': {
    pourquoi: 'Une hiérarchie de titres désordonnée nuit à la compréhension du contenu.',
    effort: 'moyen', axe: 'seo',
  },
  'aucune image': {
    pourquoi: 'Une page sans visuel est souvent moins engageante et moins partagée.',
    effort: 'rapide', axe: 'seo',
  },
  'images sans attribut alt': {
    pourquoi: 'Sans alt, les images sont invisibles pour Google Images et les lecteurs d’écran.',
    effort: 'rapide', axe: 'seo',
  },
  'url canonique manquante': {
    pourquoi: 'Sans canonical, Google peut considérer des pages comme dupliquées et diluer leur référencement.',
    effort: 'rapide', axe: 'technique',
  },
  'attribut lang manquant': {
    pourquoi: 'Sans langue déclarée, l’accessibilité et le ciblage linguistique sont dégradés.',
    effort: 'rapide', axe: 'technique',
  },
  'viewport manquant': {
    pourquoi: 'Sans viewport, l’affichage mobile est cassé, ce que Google pénalise fortement.',
    effort: 'rapide', axe: 'mobile',
  },
  'score seo faible': {
    pourquoi: 'Un référencement on-page faible limite directement la visibilité naturelle.',
    effort: 'moyen', axe: 'seo',
  },
  'seo technique faible': {
    pourquoi: 'Des problèmes techniques freinent l’indexation et le bon positionnement.',
    effort: 'moyen', axe: 'seo',
  },
  'performance mobile faible': {
    pourquoi: 'La majorité des visites se font sur mobile : une page lente fait fuir les visiteurs avant qu’ils voient l’offre.',
    effort: 'chantier', axe: 'performance',
  },
  'performance desktop faible': {
    pourquoi: 'Une page lente sur ordinateur dégrade l’expérience et le référencement.',
    effort: 'chantier', axe: 'performance',
  },
  'erreur pagespeed': {
    pourquoi: 'La performance n’a pas pu être mesurée ; impossible de garantir une bonne expérience.',
    effort: 'variable', axe: 'performance',
  },
  'accessibilite faible': {
    pourquoi: 'Un site peu accessible exclut une partie des visiteurs et présente un risque légal.',
    effort: 'chantier', axe: 'accessibilite',
  },
  'balises open graph manquantes': {
    pourquoi: 'Sans Open Graph, les partages sur les réseaux sociaux s’affichent sans aperçu attractif.',
    effort: 'rapide', axe: 'social',
  },
  'og:title manquant': {
    pourquoi: 'Le titre de partage social est absent, l’aperçu est moins engageant.',
    effort: 'rapide', axe: 'social',
  },
  'og:description manquant': {
    pourquoi: 'La description de partage social est absente.',
    effort: 'rapide', axe: 'social',
  },
  'og:image manquant': {
    pourquoi: 'Sans image de partage, les liens vers le site sont peu visibles dans les fils sociaux.',
    effort: 'rapide', axe: 'social',
  },
};

// ── 3. Critères SEO on-page (clé = critère de seoCriteria.js) ──────────────────

export const SEO_CRITERIA_DICT = {
  title: {
    label: 'Balise titre',
    pourquoi: 'Le titre est l’élément le plus déterminant pour le positionnement et le clic dans Google.',
    corriger: 'Rédiger un titre unique de 50-70 caractères contenant le mot-clé principal.',
    effort: 'rapide', axe: 'seo',
  },
  meta: {
    label: 'Meta description',
    pourquoi: 'Elle influence le taux de clic depuis les résultats de recherche.',
    corriger: 'Rédiger une description de 120-170 caractères, claire et incitative.',
    effort: 'rapide', axe: 'seo',
  },
  h1: {
    label: 'Titre principal (H1)',
    pourquoi: 'Le H1 annonce le sujet de la page à Google et aux visiteurs.',
    corriger: 'Ajouter un unique H1 décrivant le contenu de la page.',
    effort: 'rapide', axe: 'seo',
  },
  images: {
    label: 'Textes alternatifs des images',
    pourquoi: 'Les images sans alt sont invisibles pour Google Images et l’accessibilité.',
    corriger: 'Ajouter un attribut alt descriptif à chaque image porteuse de sens.',
    effort: 'rapide', axe: 'seo',
  },
  canon: {
    label: 'URL canonique',
    pourquoi: 'La balise canonical évite que Google considère des pages comme du contenu dupliqué.',
    corriger: 'Déclarer une URL canonique sur chaque page.',
    effort: 'rapide', axe: 'technique',
  },
};

/** Audits « métrique » (LCP, FCP…) : mesures, pas des actions techniques détaillées. */
export const LIGHTHOUSE_METRIC_IDS = new Set([
  'largest-contentful-paint',
  'first-contentful-paint',
  'interactive',
  'max-potential-fid',
  'total-blocking-time',
  'speed-index',
  'cumulative-layout-shift',
  'first-meaningful-paint',
  'interaction-to-next-paint',
  'experimental-interaction-to-next-paint',
]);

export function isLighthouseMetricAudit(id) {
  return LIGHTHOUSE_METRIC_IDS.has(id) || LIGHTHOUSE_DICT[id]?.kind === 'metric';
}

/** Découpe définition + impact pour les métriques (2 phrases dans pourquoi). */
export function parseMetricPourquoi(pourquoi) {
  const text = String(pourquoi || '').trim();
  if (!text) return { definition: '', impactWhy: '' };
  const match = text.match(/^(.+?\.\s+)(.+)$/s);
  if (match) {
    return { definition: match[1].trim(), impactWhy: match[2].trim() };
  }
  return { definition: text, impactWhy: '' };
}

// ── Helpers de traduction ──────────────────────────────────────────────────────

/** Normalise une catégorie d'erreur backend en clé stable (sans accents, sans compteur). */
export function normalizeCategoryKey(category) {
  return String(category || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s*\(.*?\)\s*/g, ' ') // retire « (3) », « (mobile) », etc.
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Traduit une opportunité PageSpeed (issue de getRecommendations) en objet client.
 * Fallback : conserve le titre/description Lighthouse si l'ID est inconnu.
 * @param {{id:string, title?:string, description?:string, displayValue?:string, score?:number}} rec
 */
export function translateLighthouse(rec) {
  if (!rec) return null;
  const entry = LIGHTHOUSE_DICT[rec.id];
  if (entry) {
    const measure = rec.displayValue ? ` Mesure actuelle : ${rec.displayValue}.` : '';
    let definition = '';
    let impactWhy = '';
    let pourquoi = entry.pourquoi;
    if (entry.kind === 'metric') {
      const parsed = parseMetricPourquoi(entry.pourquoi);
      definition = entry.definition || parsed.definition;
      impactWhy = entry.impactWhy || parsed.impactWhy;
      const parts = [definition, impactWhy].filter(Boolean);
      pourquoi = parts.join(' ').trim() + measure;
    }
    return {
      id: rec.id,
      label: entry.label,
      definition,
      impactWhy,
      pourquoi,
      corriger: entry.corriger,
      effort: entry.effort,
      axe: entry.axe,
      kind: entry.kind,
      displayValue: rec.displayValue || '',
      score: rec.score,
      known: true,
    };
  }
  return {
    id: rec.id,
    label: rec.title || rec.id,
    pourquoi: '',
    corriger: '',
    effort: 'variable',
    axe: 'performance',
    displayValue: rec.displayValue || '',
    score: rec.score,
    known: false,
    /** Description Lighthouse (souvent HTML avec liens) — à afficher via dangerouslySetInnerHTML. */
    descriptionHtml: rec.description || '',
  };
}

/**
 * Traduit une ligne d'erreur technique (issue de flattenTechnicalErrors) en objet client.
 * Conserve la recommandation backend pour « corriger », complète impact + effort.
 * @param {{category?:string, recommendation?:string, message?:string, type?:string, severity?:string, url?:string}} row
 */
export function translateError(row) {
  if (!row) return null;
  const key = normalizeCategoryKey(row.category);
  const entry = ERROR_DICT[key];
  return {
    label: row.category || 'Problème détecté',
    pourquoi: entry?.pourquoi || '',
    corriger: row.recommendation || '',
    effort: entry?.effort || 'variable',
    axe: entry?.axe || row.type || 'technique',
    severity: row.severity || 'low',
    message: row.message || '',
    url: row.url,
    known: !!entry,
  };
}

/** Retire la syntaxe markdown des liens (Lighthouse) pour un rendu texte propre. */
export function stripMarkdown(text) {
  if (!text) return '';
  return String(text)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/\s*\[Learn more.*?\]?\.?\s*$/i, '')
    .trim();
}

/** Extrait le texte visible d’un fragment HTML (aperçu lisible). */
export function stripHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

const SNIPPET_TAG_LABELS = {
  p: 'Paragraphe',
  div: 'Bloc',
  span: 'Texte',
  a: 'Lien',
  h1: 'Titre principal',
  h2: 'Sous-titre',
  h3: 'Titre',
  h4: 'Titre',
  h5: 'Titre',
  h6: 'Titre',
  button: 'Bouton',
  li: 'Élément de liste',
  label: 'Label',
  th: 'Cellule de tableau',
  td: 'Cellule de tableau',
};

/** Libellé court à partir de la balise Lighthouse (souvent balise ouvrante seule). */
export function lighthouseSnippetMeta(snippet) {
  const raw = (snippet || '').trim();
  if (!raw) return null;
  const tagMatch = raw.match(/^<(\w+)/i);
  const classMatch = raw.match(/class=["']([^"']+)["']/i);
  const tag = tagMatch?.[1]?.toLowerCase();
  const parts = [];
  if (tag) parts.push(SNIPPET_TAG_LABELS[tag] || tag);
  if (classMatch) {
    const firstClass = classMatch[1].split(/\s+/).filter(Boolean)[0];
    if (firstClass) parts.push(firstClass);
  }
  return parts.length ? parts.join(' · ') : null;
}

function isSafeImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url.trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** Extrait l’URL src (ou premier candidat srcset) d’un snippet <img>. */
export function extractImgSrcFromSnippet(snippet) {
  const raw = (snippet || '').trim();
  if (!/^<img\b/i.test(raw)) return null;
  const srcMatch = raw.match(/\bsrc=["']([^"']+)["']/i);
  if (srcMatch?.[1]) return srcMatch[1].trim();
  const srcsetMatch = raw.match(/\bsrcset=["']([^"']+)["']/i);
  if (srcsetMatch?.[1]) {
    const first = srcsetMatch[1].split(',')[0].trim().split(/\s+/)[0];
    return first || null;
  }
  return null;
}

function fileNameFromUrl(url) {
  if (!url) return '';
  try {
    return decodeURIComponent(new URL(url).pathname.split('/').pop() || '');
  } catch {
    return String(url).split('/').pop()?.split('?')[0] || '';
  }
}

/** Associe un fichier Lighthouse à un snippet <img> (nom partiel, index, etc.). */
function fileMatchesSnippet(file, cs) {
  if (!file?.url || !isSafeImageUrl(file.url)) return false;
  const fileBase = (file.name || fileNameFromUrl(file.url)).toLowerCase();
  const rawSrc = extractImgSrcFromSnippet(cs?.snippet);
  if (rawSrc) {
    const srcBase = rawSrc.replace(/\.\.\.+$/, '').split('/').pop()?.split('?')[0]?.toLowerCase();
    if (srcBase && fileBase) {
      if (srcBase === fileBase) return true;
      const srcStem = srcBase.replace(/\.[a-z0-9]+$/i, '');
      const fileStem = fileBase.replace(/\.[a-z0-9]+$/i, '');
      if (srcStem.length >= 4 && (fileBase.includes(srcStem) || srcStem.includes(fileStem))) return true;
    }
  }
  return cs?.index != null && file.index === cs.index;
}

function extractSrcsetUrls(snippet) {
  const raw = (snippet || '').trim();
  const srcsetMatch = raw.match(/\bsrcset=["']([^"']+)["']/i);
  if (!srcsetMatch?.[1]) return [];
  return srcsetMatch[1]
    .split(',')
    .map((part) => part.trim().split(/\s+/)[0])
    .filter((u) => isSafeImageUrl(u));
}

/**
 * Résout l’URL d’aperçu image (snippet parfois tronqué par Lighthouse).
 * @param {{ snippet?: string, index?: number, resourceUrl?: string }} cs
 * @param {{ url?: string, name?: string, index?: number }[]} [files]
 */
export function resolveSnippetImageUrl(cs, files = []) {
  if (isSafeImageUrl(cs?.resourceUrl)) return cs.resourceUrl;

  const rawSrc = extractImgSrcFromSnippet(cs?.snippet);
  if (rawSrc && !rawSrc.includes('...') && isSafeImageUrl(rawSrc)) return rawSrc;

  for (const u of extractSrcsetUrls(cs?.snippet)) {
    if (isSafeImageUrl(u)) return u;
  }

  const list = files || [];
  const byMatch = list.find((f) => fileMatchesSnippet(f, cs));
  if (byMatch?.url) return byMatch.url;

  if (rawSrc?.includes('...')) {
    const prefix = rawSrc.replace(/\.\.\.+$/, '').split('/').pop()?.toLowerCase();
    if (prefix && prefix.length >= 4) {
      const partial = list.find((f) => {
        const name = (f.name || fileNameFromUrl(f.url)).toLowerCase();
        return name.includes(prefix) || prefix.includes(name.replace(/\.[a-z0-9]+$/i, ''));
      });
      if (partial?.url && isSafeImageUrl(partial.url)) return partial.url;
    }
  }

  const byIndex = list.find((f) => f.index === cs?.index && isSafeImageUrl(f.url));
  if (byIndex?.url) return byIndex.url;

  return null;
}

/**
 * Aperçu d’un élément Lighthouse : le snippet HTML est souvent une balise vide
 * (ex. `<p class="foo">`) — le texte visible est dans nodeLabel.
 * @returns {{ mode: 'html'|'text'|'tag-only'|'image'|'empty', html?: string, text?: string, tag?: string, imageUrl?: string }}
 */
export function getLighthouseElementPreview(cs, files = []) {
  const label = (cs?.nodeLabel || '').trim();
  const rawSnippet = (cs?.snippet || '').trim();

  if (/^<img\b/i.test(rawSnippet)) {
    const imageUrl = resolveSnippetImageUrl(cs, files);
    return { mode: 'image', imageUrl: imageUrl || null, tag: rawSnippet, label };
  }

  const visibleFromSnippet = stripHtml(rawSnippet);

  if (visibleFromSnippet.length >= 2) {
    return { mode: 'html', html: rawSnippet, text: visibleFromSnippet };
  }
  if (label) {
    return { mode: 'text', text: label, tag: rawSnippet || null };
  }
  if (rawSnippet) {
    return { mode: 'tag-only', tag: rawSnippet };
  }
  return { mode: 'empty' };
}
